import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import MemberAvatar from '@/components/member-avatar';
import { Popover, menuRow } from '@/components/ui/Popover';
import { Cog, Laptop, Members, Plug, Plus, ReportsIcon, SignOut } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';
import { useNewMember } from '@/components/new-member';
import { useIdentity } from './identity';
import SetupMcpModal from './setup-mcp-modal';
import GetDesktopAppModal from './get-desktop-app-modal';
import DesktopUpdatePanel from './desktop-update-panel';
import { isDesktopApp } from './desktop-updates';
import { useDesktopNotifications } from './desktop-notifications';

/** Header account chip: your avatar, opening a small menu with Members, MCP setup + sign-out. */
export default function IdentityMenu() {
  const { me, oidc, signOut } = useIdentity();
  const navigate = useNavigate();
  const openNewMember = useNewMember();
  const canManageMembers = !oidc || me?.role === 'admin';
  // The modal lives outside the Popover so it stays mounted after the menu closes on click.
  const [mcpOpen, setMcpOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  // Opening the modal unmounts the menu row it was clicked from, so hand the Modal this
  // still-mounted trigger to return focus to when it closes.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const acctToggle = useRef<(() => void) | null>(null); // captured Popover toggle, for the strip's chip
  const acctClose = useRef<(() => void) | null>(null); // captured Popover close, for tab-switch dismiss
  // Desktop: surface @mentions/assignments as native OS notifications (no-op in a browser).
  useDesktopNotifications(me?.id);
  // The strip's account chip opens THIS menu via window.__sparkOpenAccount; the shell closes it on tab
  // switch via window.__sparkCloseAccount.
  useEffect(() => {
    const w = window as { __sparkOpenAccount?: () => void; __sparkCloseAccount?: () => void; __sparkCloseBell?: () => void };
    w.__sparkOpenAccount = () => {
      w.__sparkCloseBell?.(); // the two strip flyouts are mutually exclusive
      acctToggle.current?.();
    };
    w.__sparkCloseAccount = () => acctClose.current?.();
    return () => {
      delete w.__sparkOpenAccount;
      delete w.__sparkCloseAccount;
    };
  }, []);
  if (!me) return null;
  return (
    <>
      <Popover
        // Desktop: centre the menu under the strip's account chip; right-align in the browser header.
        align={isDesktopApp() ? 'center' : 'right'}
        // Desktop: portal so the menu survives the strip's hidden (opacity-0) trigger wrapper.
        portal={isDesktopApp()}
        // Grow to the full viewport height when there's room (the menu is taller than the default
        // 320px cap), only scrolling on short screens. Header is 56px + the 6px drop gap.
        panelClassName="!max-h-[calc(100vh-72px)]"
        trigger={({ toggle, open, close }) => {
          acctToggle.current = toggle;
          acctClose.current = close;
          return (
          <button
            ref={triggerRef}
            type="button"
            onClick={toggle}
            aria-label="Account"
            className={cn(
              'flex h-9 items-center gap-2 rounded-full pl-1 pr-2.5 transition-colors hover:bg-layer-strong',
              open && 'bg-layer-strong',
            )}
          >
            <MemberAvatar member={me} size={28} />
            <span className="hidden max-w-[120px] truncate text-[13px] font-medium text-fg-soft sm:block">
              {me.name}
            </span>
          </button>
          );
        }}
      >
        {(close) => (
          <div className="min-w-[200px]">
            <div className="flex items-center gap-2.5 px-2 py-1.5">
              <MemberAvatar member={me} size={32} />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[13px] font-semibold text-fg-strong">{me.name}</span>
                  {me.role === 'admin' ? (
                    <span className="shrink-0 rounded bg-brand/15 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-brand">
                      Admin
                    </span>
                  ) : null}
                </div>
                {me.email ? <div className="truncate text-[11px] text-fg-faint">{me.email}</div> : null}
              </div>
            </div>
            <div className="my-1 h-px bg-line" />
            <button
              type="button"
              onClick={() => {
                close();
                navigate('/reports');
              }}
              className={cn(menuRow, 'text-fg-soft')}
            >
              <ReportsIcon className="shrink-0 text-fg-muted" />
              Reports
            </button>
            <button
              type="button"
              onClick={() => {
                close();
                navigate('/members');
              }}
              className={cn(menuRow, 'text-fg-soft')}
            >
              <Members className="shrink-0 text-fg-muted" />
              Members
            </button>
            {canManageMembers ? (
              <button
                type="button"
                onClick={() => {
                  close();
                  openNewMember?.('');
                }}
                className={cn(menuRow, 'text-fg-soft')}
              >
                <Plus className="shrink-0 text-fg-muted" />
                New member
              </button>
            ) : null}
            <div className="my-1 h-px bg-line" />
            <button
              type="button"
              onClick={() => {
                close();
                navigate('/settings');
              }}
              className={cn(menuRow, 'text-fg-soft')}
            >
              <Cog className="shrink-0 text-fg-muted" />
              Settings
            </button>
            {isDesktopApp() ? null : (
              <button
                type="button"
                onClick={() => {
                  close();
                  setDesktopOpen(true);
                }}
                className={cn(menuRow, 'text-fg-soft')}
              >
                <Laptop className="shrink-0 text-fg-muted" />
                Get desktop app
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                close();
                setMcpOpen(true);
              }}
              className={cn(menuRow, 'text-fg-soft')}
            >
              <Plug className="shrink-0 text-fg-muted" />
              Set up MCP
            </button>
            <a
              href="/api-docs.html"
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => close()}
              className={cn(menuRow, 'text-fg-soft no-underline')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-fg-muted"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
              API docs
            </a>
            {/* Desktop app update controls (only inside the desktop shell). */}
            {isDesktopApp() ? (
              <>
                <div className="my-1 h-px bg-line" />
                <DesktopUpdatePanel variant="menu" />
              </>
            ) : null}
            <button
              type="button"
              onClick={() => {
                close();
                signOut();
              }}
              className={cn(menuRow, 'text-fg-soft')}
            >
              <SignOut className="shrink-0 text-fg-muted" />
              Sign out
            </button>
          </div>
        )}
      </Popover>
      <SetupMcpModal open={mcpOpen} onClose={() => setMcpOpen(false)} returnFocusTo={triggerRef} />
      <GetDesktopAppModal open={desktopOpen} onClose={() => setDesktopOpen(false)} returnFocusTo={triggerRef} />
    </>
  );
}
